import { getCookieConsentFromHeader, getCookieConsentSourceFromHeader } from "./cookieConsent";

export const LINKEDIN_ADS_CLICK_ID_QUERY_PARAM = "li_fat_id";
export const LINKEDIN_ADS_CLICK_ID_STORAGE_KEY = "tapestry:linkedInAds:li_fat_id";
const INTERNAL_URL_ORIGIN = "https://ads.openai.com";
const MAX_LINKEDIN_ADS_CLICK_ID_LENGTH = 256;
const LINKEDIN_ADS_ROLLOUT_COUNTRIES = new Set(["AU", "CA", "NZ", "US"]);

export function isLinkedInAdsRolloutCountry(countryCode: string | undefined): boolean {
  return countryCode != null && LINKEDIN_ADS_ROLLOUT_COUNTRIES.has(countryCode.toUpperCase());
}

export function isLinkedInAdsServerAttributionEligible({
  cookieHeader,
  globalPrivacyControlHeader,
  countryHeader,
}: {
  cookieHeader?: string;
  globalPrivacyControlHeader?: string | string[] | null;
  countryHeader?: string | string[] | null;
}): boolean {
  // Server redirects can only trust explicit signed-out cookie consent.
  // Authenticated canonical consent is reconciled client-side before capture.
  const globalPrivacyControl = getSingleHeaderValue(globalPrivacyControlHeader);
  const countryCode = getSingleHeaderValue(countryHeader)?.trim() || undefined;
  return (
    globalPrivacyControl !== "1" &&
    getCookieConsentFromHeader(cookieHeader ?? "", "marketing_consent") === "agreed" &&
    getCookieConsentSourceFromHeader(cookieHeader ?? "", "marketing_consent_source") ===
      "signed_out" &&
    isLinkedInAdsRolloutCountry(countryCode)
  );
}

export function getLinkedInAdsClickId(searchParams: URLSearchParams): string | undefined {
  const values = searchParams.getAll(LINKEDIN_ADS_CLICK_ID_QUERY_PARAM);
  if (values.length !== 1) return undefined;

  const rawValue = values[0];
  const value = rawValue?.trim();
  if (!value || value !== rawValue || !isValidLinkedInAdsClickId(value)) {
    return undefined;
  }
  return value;
}

export function appendLinkedInAdsClickIdToPath(path: string, clickId: string | undefined): string {
  try {
    const url = new URL(path, INTERNAL_URL_ORIGIN);
    if (url.origin !== INTERNAL_URL_ORIGIN) return path;

    url.searchParams.delete(LINKEDIN_ADS_CLICK_ID_QUERY_PARAM);
    if (clickId && isValidLinkedInAdsClickId(clickId)) {
      url.searchParams.set(LINKEDIN_ADS_CLICK_ID_QUERY_PARAM, clickId);
    }
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return path;
  }
}

function getSingleHeaderValue(
  headerValue: string | string[] | null | undefined,
): string | undefined {
  return Array.isArray(headerValue) ? headerValue[0] : headerValue ?? undefined;
}

function isValidLinkedInAdsClickId(value: string): boolean {
  return (
    value.trim() === value &&
    value.length > 0 &&
    value.length <= MAX_LINKEDIN_ADS_CLICK_ID_LENGTH &&
    !Array.from(value).some((character) => {
      const codePoint = character.codePointAt(0) ?? 0;
      return codePoint <= 0x1f || codePoint === 0x7f;
    })
  );
}
